// Create new analysis job
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import analysisJobApi from '../services/analysisJobApi';
import scrapeJobApi from '../services/scrapeJobApi';
import fileSetApi from '../services/fileSetApi';
import fileApi from '../services/fileApi';
import Loading from '../components/Loading';

// Default number of files to keep when filtering
const DEFAULT_FILTER_COUNT = 80;

const NewAnalysisJob = () => {
  const navigate = useNavigate();
  // Get initial form data from local storage or use defaults
  const getInitialFormData = () => {
    const savedData = localStorage.getItem('analysisJobFormData');
    const defaultData = {
      files: [],
      scrapeJobParts: [],
      prompt: "Analyze this content and provide insights on structure, quality, and potential improvements.",
      modelName: "gpt-4.1",
      temperature: 0.7,
      topP: 0.95,
      maxOutputTokens: 4000
    };
    
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        return parsedData;
      } catch (e) {
        console.error('Error parsing saved form data:', e);
        return defaultData;
      }
    }
    return defaultData;
  };

  const [formData, setFormData] = useState(getInitialFormData);
  
  // Get initial showAdvanced setting from localStorage
  const initialShowAdvanced = (() => {
    try {
      const savedSelections = localStorage.getItem('analysisJobSelections');
      if (savedSelections) {
        const parsed = JSON.parse(savedSelections);
        return parsed.showAdvanced;
      }
    } catch (e) {
      console.error('Error loading saved selections:', e);
    }
    return false;
  })();
  
  const [showAdvanced, setShowAdvanced] = useState(initialShowAdvanced);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scrapeJobs, setScrapeJobs] = useState([]);
  const [fileSets, setFileSets] = useState([]);
  
  // Track expanded state for scrape jobs and file sets (not persisted)
  const [expandedScrapeJobs, setExpandedScrapeJobs] = useState({});
  const [expandedFileTypes, setExpandedFileTypes] = useState({});
  const [expandedFileSets, setExpandedFileSets] = useState({});
  
  // Store files by scrape job and organized by type
  const [filesByScrapeJob, setFilesByScrapeJob] = useState({});
  
  // Track loading states
  const [loadingScrapeJobFiles, setLoadingScrapeJobFiles] = useState({});
  
  // Store file set files
  const [filesByFileSet, setFilesByFileSet] = useState({});
  
  // Store all selected files
  const [selectedFilesById, setSelectedFilesById] = useState({});
  
  // For filtering files to most relevant (by scrapeJobId-fileType)
  const [filterCounts, setFilterCounts] = useState({});
  const [isFilteringFiles, setIsFilteringFiles] = useState({});
  

  // Get available data sources on component mount
  useEffect(() => {
    const fetchDataSources = async () => {
      try {
        setIsLoading(true);
        
        const [scrapeJobsData, fileSetsData] = await Promise.all([
          scrapeJobApi.getScrapeJobs({ all: true }),
          fileSetApi.getFileSets()
        ]);
        
        // Filter only completed scrape jobs
        const completedScrapeJobs = scrapeJobsData.filter(job => job.status === 'completed');
        
        setScrapeJobs(completedScrapeJobs);
        setFileSets(fileSetsData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data sources:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataSources();
  }, []);

  // Function to load files for a file set when expanded
  const loadFilesForFileSet = async (fileSetId) => {
    // Skip if we already have the files
    if (filesByFileSet[fileSetId]) {
      return;
    }
    
    try {
      // Fetch the files
      const fileSet = await fileSetApi.getFileSet(fileSetId);
      const files = fileSet.files || [];
      
      // Store the files in our cache
      setFilesByFileSet(prev => ({
        ...prev,
        [fileSetId]: files
      }));
    } catch (err) {
      console.error('Error fetching files for file set:', err);
      // Add an empty array to avoid repeated failed requests
      setFilesByFileSet(prev => ({
        ...prev,
        [fileSetId]: []
      }));
    }
  };
  
  // No need to load files for previously expanded file sets since we don't persist expanded state anymore

  // Save form data to local storage when it changes
  useEffect(() => {
    // Save all form data including file selections
    localStorage.setItem('analysisJobFormData', JSON.stringify(formData));
  }, [formData]);

  // Save advanced settings state only
  useEffect(() => {
    const selectionsToSave = {
      showAdvanced
    };
    localStorage.setItem('analysisJobSelections', JSON.stringify(selectionsToSave));
  }, [showAdvanced]);

  // showAdvanced is now initialized directly from localStorage in useState

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    // Convert number inputs to numbers
    const parsedValue = type === 'number' ? (value ? parseFloat(value) : '') : value;
    
    setFormData({
      ...formData,
      [name]: parsedValue
    });
  };

  // Track last selected file for Shift+click multi-selection
  const [lastSelectedFileId, setLastSelectedFileId] = useState(null);
  
  // Handle file selection with support for Ctrl/Shift multi-selection
  const handleFileSelection = (fileId, event = {}, filesList = []) => {
    const isCtrlPressed = event.ctrlKey || event.metaKey;
    const isShiftPressed = event.shiftKey;
    
    if (isShiftPressed && lastSelectedFileId && filesList.length > 0) {
      // Get the indices of current and last selected files
      const currentIndex = filesList.findIndex(file => file.fileId === fileId);
      const lastIndex = filesList.findIndex(file => file.fileId === lastSelectedFileId);
      
      // Invalid indices or same file
      if (currentIndex === -1 || lastIndex === -1) {
        handleSingleFileSelection(fileId, isCtrlPressed);
        return;
      }
      
      // Get range of files between the two selected files
      const startIndex = Math.min(currentIndex, lastIndex);
      const endIndex = Math.max(currentIndex, lastIndex);
      const filesToToggle = filesList.slice(startIndex, endIndex + 1);
      
      setSelectedFilesById(prev => {
        // Always keep existing selections when using multi-select
        const updated = { ...prev };
        
        // Check if all files in range are selected
        const allSelected = filesToToggle.every(file => prev[file.fileId]);
        
        filesToToggle.forEach(file => {
          if (allSelected) {
            delete updated[file.fileId];
          } else {
            updated[file.fileId] = true;
          }
        });
        
        return updated;
      });
    } else {
      handleSingleFileSelection(fileId, isCtrlPressed);
    }
    
    // Remember this file id for future Shift+click
    setLastSelectedFileId(fileId);
  };
  
  // Helper for single file selection
  const handleSingleFileSelection = (fileId, keepExisting = false) => {
    setSelectedFilesById(prev => {
      // If Ctrl is pressed, keep all existing selections, otherwise just toggle the clicked one
      const updated = keepExisting ? { ...prev } : { ...prev };
      
      if (prev[fileId]) {
        delete updated[fileId];
      } else {
        updated[fileId] = true;
      }
      
      return updated;
    });
  };

  // Function to toggle expand/collapse a scrape job
  const toggleScrapeJob = async (scrapeJobId) => {
    setExpandedScrapeJobs(prev => {
      const isCurrentlyExpanded = !!prev[scrapeJobId];
      const updated = { ...prev, [scrapeJobId]: !isCurrentlyExpanded };
      
      // If expanding, load file types for this job if not already loaded
      if (!isCurrentlyExpanded && !filesByScrapeJob[scrapeJobId]) {
        loadScrapeJobFiles(scrapeJobId);
      }
      
      return updated;
    });
  };
  
  // Load files for a scrape job, organized by type
  const loadScrapeJobFiles = async (scrapeJobId) => {
    if (loadingScrapeJobFiles[scrapeJobId]) return;
    
    try {
      setLoadingScrapeJobFiles(prev => ({ ...prev, [scrapeJobId]: true }));
      
      // Get all files for this scrape job
      const files = await fileApi.getFiles({ scrapeJobId });
      
      // Group files by type
      const filesByType = fileApi.groupFilesByType(files);
      
      // Update the state
      setFilesByScrapeJob(prev => ({
        ...prev,
        [scrapeJobId]: filesByType
      }));
    } catch (err) {
      console.error('Error loading scrape job files:', err);
    } finally {
      setLoadingScrapeJobFiles(prev => ({ ...prev, [scrapeJobId]: false }));
    }
  };
  
  // Toggle expanding a file type within a scrape job
  const toggleFileType = (scrapeJobId, fileType) => {
    const key = `${scrapeJobId}-${fileType}`;
    
    setExpandedFileTypes(prev => {
      const isCurrentlyExpanded = !!prev[key];
      return { ...prev, [key]: !isCurrentlyExpanded };
    });
    
    // Initialize filter count if not already set
    setFilterCounts(prev => {
      if (prev[key] === undefined) {
        return { ...prev, [key]: DEFAULT_FILTER_COUNT };
      }
      return prev;
    });
  };
  // console.log('herrrre')
  // Filter files to most relevant using AI
  const handleFilterFiles = async (scrapeJobId, fileType, files) => {
    // console.log('in handleFilterFiles');
    const key = `${scrapeJobId}-${fileType}`;
    const count = filterCounts[key] || DEFAULT_FILTER_COUNT;
    
    if (!files || files.length === 0 || count >= files.length) {
      return; // No need to filter if count is greater than available files
    }
    
    try {
      setIsFilteringFiles(prev => ({ ...prev, [key]: true }));
      
      // Get all file IDs for this type
      const fileIds = files.map(file => file.fileId);
      // console.log('fileIds');
      // console.log(fileIds);
      
      // Call the API to filter files
      const result = await fileApi.filterFiles(fileIds, count);
      // console.log('result');
      // console.log(result);

      if (result && result.filteredFileIds) {
        // console.log(result.filteredFileId);
      // Select only the filtered files
        setSelectedFilesById(prev => {
          const updated = { ...prev };
          
          // First unselect all files of this type
          files.forEach(file => {
            delete updated[file.fileId];
          });
          
          // Then select only the filtered files
          result.filteredFileIds.forEach(fileId => {
            updated[fileId] = true;
          });
          // console.log('updated')
          // console.log(updated)
          return updated;
        });
      }
    } catch (error) {
      console.error('Error filtering files:', error);
    } finally {
      setIsFilteringFiles(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Toggle selection of all files of a type in a scrape job
  const toggleAllFilesOfType = (scrapeJobId, fileType, files) => {
    // Check if all files of this type are currently selected
    const allSelected = files.every(file => selectedFilesById[file.fileId]);
    
    // Toggle selection based on current state
    setSelectedFilesById(prev => {
      const updated = { ...prev };
      
      files.forEach(file => {
        if (allSelected) {
          // Deselect all
          delete updated[file.fileId];
        } else {
          // Select all
          updated[file.fileId] = true;
        }
      });
      
      return updated;
    });
  };
  

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get selected files
    const selectedFiles = Object.keys(selectedFilesById);
    
    // Basic validation
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to analyze');
      return;
    }
    
    if (!formData.prompt.trim()) {
      setError('Analysis prompt is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Create the analysis job
      const newJob = await analysisJobApi.createAnalysisJob({
        files: selectedFiles,
        prompt: formData.prompt,
        modelName: formData.modelName,
        temperature: formData.temperature,
        topP: formData.topP,
        maxOutputTokens: formData.maxOutputTokens
      });
      
      // Keep all form data in local storage for future use
      // (we're already saving it in the useEffect hooks)
      
      // Redirect to the job details page
      navigate(`/advanced/analysis-jobs/${newJob.jobId}`);
    } catch (err) {
      setError(err.message);
      console.error('Error creating analysis job:', err);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading data sources..." />;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Analysis Job</h1>
        <p className="mt-2 text-sm text-gray-700">
          Create a new AI analysis job by selecting content sources and configuring parameters.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error: {error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-8">
            {/* Content Source Selection */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Step 1: Select Content Sources</h2>
              
              {/* Option 1: Select from Scrape Jobs */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-700 mb-2">Select content from completed scrape jobs</h3>
                
                {scrapeJobs.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-4">No completed scrape jobs available.</p>
                ) : (
                  <div className="space-y-3">
                    {/* List of scrape jobs */}
                    {scrapeJobs.map(job => (
                      <div key={job.jobId} className="border border-gray-200 rounded-md overflow-hidden">
                        {/* Scrape job header - clickable to expand */}
                        <div 
                          className="bg-gray-50 p-3 flex justify-between items-center cursor-pointer"
                          onClick={() => toggleScrapeJob(job.jobId)}
                        >
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 truncate" title={job.targetUrl}>
                              {job.targetUrl}
                            </h4>
                            <p className="text-xs text-gray-500">
                              Created: {new Date(job.createdAt).toLocaleDateString()} {new Date(job.createdAt).toLocaleTimeString()}
                            </p>
                          </div>
                          {/* Expand/collapse arrow */}
                          <button className="text-gray-500 p-1">
                            {expandedScrapeJobs[job.jobId] ? (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        </div>
                        
                        {/* Expanded content - file types and files */}
                        {expandedScrapeJobs[job.jobId] && (
                          <div className="p-3">
                            {/* Loading indicator */}
                            {loadingScrapeJobFiles[job.jobId] && (
                              <div className="py-4 flex justify-center">
                                <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            )}
                            
                            {/* Display file types in the specified order */}
                            {filesByScrapeJob[job.jobId] && ['screenshot', 'html', 'reduced-level1-html', 'reduced-level2-html'].map(fileType => {
                              const files = filesByScrapeJob[job.jobId][fileType] || [];
                              if (files.length === 0) return null;
                              return (
                                <div key={fileType} className="mb-3 border border-gray-200 rounded">
                                  {/* File type header */}
                                  <div 
                                    className="bg-gray-100 px-3 py-2 flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFileType(job.jobId, fileType)}
                                  >
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                        checked={files.every(file => selectedFilesById[file.fileId])}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          toggleAllFilesOfType(job.jobId, fileType, files);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="font-medium text-gray-800">
                                        {fileType === 'html' && 'HTML'}
                                        {fileType === 'reduced-level1-html' && 'Reduced HTML (L1)'}
                                        {fileType === 'reduced-level2-html' && 'Reduced HTML (L2)'}
                                        {fileType === 'screenshot' && 'Screenshots'}
                                        {!['html', 'reduced-level1-html', 'reduced-level2-html', 'screenshot'].includes(fileType) && fileType}
                                        <span className="ml-2 text-gray-500 text-sm">({files.length} files)</span>
                                      </span>
                                    </div>
                                    <button className="text-gray-500">
                                      {expandedFileTypes[`${job.jobId}-${fileType}`] ? (
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                        </svg>
                                      ) : (
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                  
                                  {/* Individual files */}
                                  {expandedFileTypes[`${job.jobId}-${fileType}`] && (
                                    <div className="p-2 max-h-60 overflow-y-auto">
                                      {/* Filter control */}
                                      <div className="mb-3 flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Filter to</span>
                                        <input 
                                          type="number" 
                                          // min="1" 
                                          // max={files.length}
                                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm" 
                                          value={filterCounts[`${job.jobId}-${fileType}`] || DEFAULT_FILTER_COUNT}
                                          onChange={(e) => {
                                            const value = Math.max(1, Math.min(files.length, parseInt(e.target.value) || DEFAULT_FILTER_COUNT));
                                            setFilterCounts(prev => ({
                                              ...prev,
                                              [`${job.jobId}-${fileType}`]: value
                                            }));
                                          }}
                                        />
                                        <span className="text-sm text-gray-600">most relevant:</span>
                                        <button
                                          className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-300"
                                          onClick={() => handleFilterFiles(job.jobId, fileType, files)}
                                          disabled={isFilteringFiles[`${job.jobId}-${fileType}`] || files.length <= (filterCounts[`${job.jobId}-${fileType}`] || DEFAULT_FILTER_COUNT)}
                                        >
                                          {isFilteringFiles[`${job.jobId}-${fileType}`] ? 'Filtering...' : 'Apply'}
                                        </button>
                                      </div>
                                      
                                      <div className="space-y-1">
                                        {files.map((file, index) => (
                                          <div 
                                            key={file.fileId} 
                                            className={`flex items-center p-1 rounded hover:bg-gray-100 ${selectedFilesById[file.fileId] ? 'bg-blue-50' : ''}`}
                                          >
                                            <input
                                              id={`file-${file.fileId}`}
                                              type="checkbox"
                                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                              checked={!!selectedFilesById[file.fileId]}
                                              onChange={(e) => handleFileSelection(file.fileId, e, files)}
                                            />
                                            <label 
                                              htmlFor={`file-${file.fileId}`} 
                                              className="ml-2 block text-sm text-gray-700 truncate cursor-pointer w-full"
                                              title={file.filename || file.url}
                                              onClick={(e) => handleFileSelection(file.fileId, e, files)}
                                            >
                                              {file.filename || file.url || `File ${index + 1}`}
                                              {file.size && <span className="text-gray-500 ml-1">({Math.round(file.size/1024)} KB)</span>}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* Display other file types that aren't in the predefined list */}
                            {filesByScrapeJob[job.jobId] && Object.entries(filesByScrapeJob[job.jobId])
                              .filter(([fileType]) => !['screenshot', 'html', 'reduced-level1-html', 'reduced-level2-html'].includes(fileType))
                              .map(([fileType, files]) => (
                                <div key={fileType} className="mb-3 border border-gray-200 rounded">
                                  {/* File type header */}
                                  <div 
                                    className="bg-gray-100 px-3 py-2 flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFileType(job.jobId, fileType)}
                                  >
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                        checked={files.every(file => selectedFilesById[file.fileId])}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          toggleAllFilesOfType(job.jobId, fileType, files);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="font-medium text-gray-800">
                                        {fileType}
                                        <span className="ml-2 text-gray-500 text-sm">({files.length} files)</span>
                                      </span>
                                    </div>
                                    <button className="text-gray-500">
                                      {expandedFileTypes[`${job.jobId}-${fileType}`] ? (
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                        </svg>
                                      ) : (
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                  
                                  {/* Individual files */}
                                  {expandedFileTypes[`${job.jobId}-${fileType}`] && (
                                    <div className="p-2 max-h-60 overflow-y-auto">
                                      {/* Filter control */}
                                      <div className="mb-3 flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Filter to</span>
                                        <input 
                                          type="number" 
                                          min="1" 
                                          // max={files.length}
                                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm" 
                                          value={filterCounts[`${job.jobId}-${fileType}`] || DEFAULT_FILTER_COUNT}
                                          onChange={(e) => {
                                            const value = Math.max(1, Math.min(files.length, parseInt(e.target.value) || DEFAULT_FILTER_COUNT));
                                            setFilterCounts(prev => ({
                                              ...prev,
                                              [`${job.jobId}-${fileType}`]: value
                                            }));
                                          }}
                                        />
                                        <span className="text-sm text-gray-600">most relevant:</span>
                                        <button
                                          className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-gray-300"
                                          onClick={() => handleFilterFiles(job.jobId, fileType, files)}
                                          disabled={isFilteringFiles[`${job.jobId}-${fileType}`] || files.length <= (filterCounts[`${job.jobId}-${fileType}`] || DEFAULT_FILTER_COUNT)}
                                        >
                                          {isFilteringFiles[`${job.jobId}-${fileType}`] ? 'Filtering...' : 'Apply'}
                                        </button>
                                      </div>
                                      
                                      <div className="space-y-1">
                                        {files.map((file, index) => (
                                          <div 
                                            key={file.fileId} 
                                            className={`flex items-center p-1 rounded hover:bg-gray-100 ${selectedFilesById[file.fileId] ? 'bg-blue-50' : ''}`}
                                          >
                                            <input
                                              id={`file-${file.fileId}`}
                                              type="checkbox"
                                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                              checked={!!selectedFilesById[file.fileId]}
                                              onChange={(e) => handleFileSelection(file.fileId, e, files)}
                                            />
                                            <label 
                                              htmlFor={`file-${file.fileId}`} 
                                              className="ml-2 block text-sm text-gray-700 truncate cursor-pointer w-full"
                                              title={file.filename || file.url}
                                              onClick={(e) => handleFileSelection(file.fileId, e, files)}
                                            >
                                              {file.filename || file.url || `File ${index + 1}`}
                                              {file.size && <span className="text-gray-500 ml-1">({Math.round(file.size/1024)} KB)</span>}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Option 2: Select from File Sets */}
              <div>
                <h3 className="text-md font-medium text-gray-700 mb-2">Select files from file sets</h3>
                
                {fileSets.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-4">No file sets available.</p>
                ) : (
                  <div className="space-y-3">
                    {/* List of file sets */}
                    {fileSets.map(fileSet => (
                      <div key={fileSet.fileSetId} className="border border-gray-200 rounded-md overflow-hidden">
                        {/* File set header - clickable to expand */}
                        <div 
                          className="bg-gray-50 p-3 flex justify-between items-center cursor-pointer"
                          onClick={() => {
                            // Toggle expanded state
                            const isCurrentlyExpanded = !!expandedFileSets[fileSet.fileSetId];
                            
                            // If we're expanding, load the files if needed
                            if (!isCurrentlyExpanded) {
                              loadFilesForFileSet(fileSet.fileSetId);
                            }
                            
                            // Toggle the expanded state
                            setExpandedFileSets(prev => {
                              return { ...prev, [fileSet.fileSetId]: !isCurrentlyExpanded };
                            });
                          }}
                        >
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 truncate" title={fileSet.name}>
                              {fileSet.name}
                            </h4>
                            <p className="text-xs text-gray-500">
                              Created: {new Date(fileSet.createdAt).toLocaleDateString()} • 
                              {fileSet.files?.length || 0} files
                            </p>
                          </div>
                          {/* Expand/collapse arrow */}
                          <button className="text-gray-500 p-1">
                            {expandedFileSets[fileSet.fileSetId] ? (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        </div>
                        
                        {/* File set content - only show when expanded */}
                        {expandedFileSets[fileSet.fileSetId] && (
                          <div className="p-3">
                            {/* Files listing */}
                            <div className="max-h-60 overflow-y-auto bg-white rounded border border-gray-200">
                              {!filesByFileSet[fileSet.fileSetId] ? (
                                <div className="py-4 flex justify-center">
                                  <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              ) : filesByFileSet[fileSet.fileSetId].length === 0 ? (
                                <p className="text-sm text-gray-500 p-3">No files available in this file set.</p>
                              ) : (
                                <div className="p-2">
                                  {/* Filter control and select all */}
                                  <div className="mb-3">
                                   
                                    
                                    <div className="flex items-center">
                                      <input
                                        id={`all-files-${fileSet.fileSetId}`}
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={filesByFileSet[fileSet.fileSetId]?.length > 0 && 
                                          filesByFileSet[fileSet.fileSetId].every(file => selectedFilesById[file.fileId])}
                                        onChange={() => {
                                          const files = filesByFileSet[fileSet.fileSetId] || [];
                                          const allSelected = files.length > 0 && 
                                            files.every(file => selectedFilesById[file.fileId]);
                                          
                                          setSelectedFilesById(prev => {
                                            const updated = { ...prev };
                                            
                                            files.forEach(file => {
                                              if (allSelected) {
                                                delete updated[file.fileId];
                                              } else {
                                                updated[file.fileId] = true;
                                              }
                                            });
                                            
                                            return updated;
                                          });
                                        }}
                                      />
                                      <label htmlFor={`all-files-${fileSet.fileSetId}`} className="ml-2 font-medium text-sm text-gray-700">
                                        Select All Files
                                      </label>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    {(filesByFileSet[fileSet.fileSetId] || []).map((file, index) => (
                                      <div 
                                        key={file.fileId} 
                                        className={`flex items-center p-1 rounded hover:bg-gray-100 ${selectedFilesById[file.fileId] ? 'bg-blue-50' : ''}`}
                                      >
                                        <input
                                          id={`fileset-file-${file.fileId}`}
                                          type="checkbox"
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                          checked={!!selectedFilesById[file.fileId]}
                                          onChange={(e) => handleFileSelection(file.fileId, e, filesByFileSet[fileSet.fileSetId] || [])}
                                        />
                                        <label 
                                          htmlFor={`fileset-file-${file.fileId}`} 
                                          className="ml-2 block text-sm text-gray-700 truncate cursor-pointer w-full"
                                          title={file.filename}
                                          onClick={(e) => handleFileSelection(file.fileId, e, filesByFileSet[fileSet.fileSetId] || [])}
                                        >
                                          {file.filename} ({file.type})
                                          {file.size && <span className="text-gray-500 ml-1">({Math.round(file.size/1024)} KB)</span>}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Analysis Configuration */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Step 2: Configure Analysis Parameters</h2>
              
              {/* Analysis Prompt */}
              <div className="mb-4">
                <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">
                  Analysis Prompt <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="prompt"
                  name="prompt"
                  rows="5"
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.prompt}
                  onChange={handleInputChange}
                  required
                ></textarea>
                <p className="mt-1 text-sm text-gray-500">
                  Instructions for the AI model on how to analyze the content.
                </p>
              </div>
              
              {/* Model Selection */}
              <div className="mb-4">
                <label htmlFor="modelName" className="block text-sm font-medium text-gray-700">
                  AI Model
                </label>
                <select
                  id="modelName"
                  name="modelName"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={formData.modelName}
                  onChange={handleInputChange}
                >
                  <option value="gpt-4.1">GPT 4.1</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                  <option value="gemini-2.5-pro-preview-03-25">Gemini 2.5 Pro</option>
                  {/* <option value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</option>
                  <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option> */}
                </select>
              </div>
              
              {/* Advanced Settings Toggle */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                  <span className="mr-1">{showAdvanced ? "Hide" : "Show"} Advanced Settings</span>
                  <svg 
                    className={`h-4 w-4 transition-transform duration-200 ${showAdvanced ? 'transform rotate-180' : ''}`} 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              {/* Advanced Settings Panel */}
              {showAdvanced && (
                <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Advanced Model Settings</h3>
                  
                  {/* Temperature */}
                  <div className="mb-4">
                    <label htmlFor="temperature" className="block text-sm font-medium text-gray-700">
                      Temperature (0.0 - 1.0)
                    </label>
                    <input
                      type="number"
                      id="temperature"
                      name="temperature"
                      min="0"
                      max="1"
                      step="0.1"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={formData.temperature}
                      onChange={handleInputChange}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Controls randomness: 0 is deterministic, 1 is most random.
                    </p>
                  </div>
                  
                  {/* Top P */}
                  <div className="mb-4">
                    <label htmlFor="topP" className="block text-sm font-medium text-gray-700">
                      Top P (0.0 - 1.0)
                    </label>
                    <input
                      type="number"
                      id="topP"
                      name="topP"
                      min="0"
                      max="1"
                      step="0.01"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={formData.topP}
                      onChange={handleInputChange}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Controls diversity: lower values focus on more likely tokens.
                    </p>
                  </div>
                  
                  {/* Max Output Tokens */}
                  <div className="mb-2">
                    <label htmlFor="maxOutputTokens" className="block text-sm font-medium text-gray-700">
                      Max Output Tokens
                    </label>
                    <input
                      type="number"
                      id="maxOutputTokens"
                      name="maxOutputTokens"
                      min="100"
                      max="16000"
                      step="100"
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={formData.maxOutputTokens}
                      onChange={handleInputChange}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Maximum length of generated response. Higher values allow for more detailed analyses.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Selection Summary */}
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-800 mb-2">Selected Content Summary:</h3>
              <ul className="list-disc list-inside text-sm text-blue-800">
                {Object.keys(selectedFilesById).length > 0 ? (
                  <li>
                    Files: {Object.keys(selectedFilesById).length} file(s) selected
                  </li>
                ) : (
                  <li className="text-red-600">No files selected</li>
                )}
              </ul>
            </div>
            
            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Link
                to="/advanced/analysis-jobs"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting || Object.keys(selectedFilesById).length === 0}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isSubmitting || Object.keys(selectedFilesById).length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Analysis'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewAnalysisJob;